import { BlendConfig, RSItem, TermOrigin } from "./types";

export interface BlendTermsArgs {
  /** Our high-value keywords, already resolved for this arm. */
  partnerTerms: string[];
  /** The single envelope Yahoo returns for all Partner Supplied Terms. */
  partnerTermsQs: string | null;
  /** Yahoo's own related terms, each with its own envelope. */
  yahooItems: RSItem[];
  config: BlendConfig;
}

/**
 * Comparison key for "is this the same term?" — case-insensitive with collapsed
 * whitespace, no stemming. Deliberately blunt: it catches the collisions a visitor
 * would notice ("Used Cars" vs "used  cars") without guessing at morphology.
 */
const normalize = (term: string): string =>
  term.trim().toLowerCase().replace(/\s+/g, " ");

/** Drops empty terms, and any later repeat of a term already kept. */
function dedupeByTerm(items: RSItem[]): RSItem[] {
  const seen = new Set<string>();
  const kept: RSItem[] = [];

  for (const item of items) {
    const key = normalize(item.term ?? "");
    if (key && !seen.has(key)) {
      seen.add(key);
      kept.push(item);
    }
  }

  return kept;
}

/**
 * Builds the rows for one render of the related-search block, mixing our Partner
 * Supplied Terms with Yahoo's own related terms (PARKING-5269).
 *
 * Ours win on collision: they carry `searchQuery`, which is the monetizing path.
 * When Yahoo returns fewer terms than the block has room for, our unused keywords
 * backfill rather than rendering a short block.
 *
 * A null `partnerTermsQs` yields no rows of ours and the result degrades to pure
 * Yahoo — the pre-blend behavior, reached without a special case.
 */
export function blendTerms({
  partnerTerms,
  partnerTermsQs,
  yahooItems,
  config,
}: BlendTermsArgs): RSItem[] {
  const { partnerTermCount, totalRows, interleave } = config;
  if (totalRows <= 0) return [];

  const ours = dedupeByTerm(
    partnerTermsQs
      ? partnerTerms.map((term) => ({
          term,
          qs: partnerTermsQs,
          searchQuery: term,
          origin: TermOrigin.PARTNER,
        }))
      : []
  );

  // Clamped low as well as high: a negative count from untyped cohort config would
  // otherwise reach slice() as a from-the-end offset and silently drop a row.
  const takeOurs = Math.max(
    0,
    Math.min(partnerTermCount, ours.length, totalRows)
  );
  const selectedOurs = ours.slice(0, takeOurs);
  const shownByUs = new Set(selectedOurs.map((item) => normalize(item.term)));

  const theirs = dedupeByTerm(yahooItems)
    .filter((item) => !shownByUs.has(normalize(item.term)))
    .map((item) => ({ ...item, origin: item.origin ?? TermOrigin.YAHOO }));
  const selectedTheirs = theirs.slice(0, Math.max(0, totalRows - takeOurs));

  const shownByThem = new Set(
    selectedTheirs.map((item) => normalize(item.term))
  );
  const backfill = ours
    .slice(takeOurs)
    .filter((item) => !shownByThem.has(normalize(item.term)))
    .slice(0, Math.max(0, totalRows - takeOurs - selectedTheirs.length));

  const oursAll = [...selectedOurs, ...backfill];

  if (!interleave) {
    return [...oursAll, ...selectedTheirs].slice(0, totalRows);
  }

  const blended: RSItem[] = [];
  const rounds = Math.max(oursAll.length, selectedTheirs.length);
  for (let i = 0; i < rounds; i++) {
    if (oursAll[i]) blended.push(oursAll[i]);
    if (selectedTheirs[i]) blended.push(selectedTheirs[i]);
  }

  return blended.slice(0, totalRows);
}
